import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Space,
  Card,
  Typography,
  Switch,
  Select
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined
} from '@ant-design/icons';

const { Title } = Typography;

interface GameType {
  id: number;
  name: string;
  display_name: string;
  memory_threshold_mb: number;
  description: string;
  icon: string | null;
  config_template: string | null;
  default_port_range: string | null;
  supports_mods: number;
  is_steam_game: number;
  steam_app_id: number | null;
  status: string;
  created_at: string;
  updated_at: string;
}

const GameManagement: React.FC = () => {
  const [gameTypes, setGameTypes] = useState<GameType[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingGame, setEditingGame] = useState<GameType | null>(null);
  const [form] = Form.useForm();

  // 获取游戏类型列表
  const fetchGameTypes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/game-types', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setGameTypes(data.data);
      } else {
        message.error(data.message || '获取游戏类型失败');
      }
    } catch (error) {
      console.error('获取游戏类型失败:', error);
      message.error('获取游戏类型失败');
    } finally {
      setLoading(false);
    }
  };

  // 保存游戏类型
  const handleSave = async (values: any) => {
    try {
      const token = localStorage.getItem('token');
      const url = editingGame 
        ? `/api/admin/game-types/${editingGame.id}`
        : '/api/admin/game-types';
      
      const method = editingGame ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(values)
      });
      
      const data = await response.json();
      if (data.success) {
        message.success(editingGame ? '游戏类型更新成功' : '游戏类型创建成功');
        setModalVisible(false);
        setEditingGame(null);
        form.resetFields();
        fetchGameTypes();
      } else {
        message.error(data.message || '操作失败');
      }
    } catch (error) {
      console.error('保存游戏类型失败:', error);
      message.error('保存游戏类型失败');
    }
  };

  // 删除游戏类型
  const handleDelete = async (id: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/game-types/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      if (data.success) {
        message.success('游戏类型删除成功');
        fetchGameTypes();
      } else {
        message.error(data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除游戏类型失败:', error);
      message.error('删除游戏类型失败');
    }
  };

  // 编辑游戏类型
  const handleEdit = (game: GameType) => {
    setEditingGame(game);
    form.setFieldsValue(game);
    setModalVisible(true);
  };

  // 添加游戏类型
  const handleAdd = () => {
    setEditingGame(null);
    form.resetFields();
    setModalVisible(true);
  };

  useEffect(() => {
    fetchGameTypes();
  }, []);

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 50,
      fixed: 'left' as const,
    },
    {
      title: '游戏名称',
      dataIndex: 'name',
      key: 'name',
      width: 80,
      fixed: 'left' as const,
    },
    {
      title: '显示名称',
      dataIndex: 'display_name',
      key: 'display_name',
      width: 120,
    },
    {
      title: '内存阈值',
      dataIndex: 'memory_threshold_mb',
      key: 'memory_threshold_mb',
      width: 90,
      render: (value: number) => `${value} MB`,
    },
    {
      title: '端口范围',
      dataIndex: 'default_port_range',
      key: 'default_port_range',
      width: 100,
      render: (value: string) => value || '未设置',
    },
    {
      title: 'Steam ID',
      dataIndex: 'steam_app_id',
      key: 'steam_app_id',
      width: 90,
      render: (value: number) => value || '非Steam',
    },
    {
      title: 'MOD支持',
      dataIndex: 'supports_mods',
      key: 'supports_mods',
      width: 70,
      render: (value: number) => value ? '是' : '否',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 60,
      render: (value: string) => (
        <span style={{ color: value === 'active' ? '#52c41a' : '#ff4d4f' }}>
          {value === 'active' ? '启用' : '禁用'}
        </span>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: 150,
      ellipsis: true,
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right' as const,
      render: (_: any, record: GameType) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个游戏类型吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={3} style={{ margin: 0 }}>游戏管理</Title>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchGameTypes}
              loading={loading}
            >
              刷新
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              添加游戏类型
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={gameTypes}
          scroll={{ x: 1000 }}
          rowKey="id"
          loading={loading}
          className="admin-game-list-table"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
        />
      </Card>

      <Modal
        title={editingGame ? '编辑游戏类型' : '添加游戏类型'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingGame(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Form.Item
            name="name"
            label="游戏名称"
            rules={[{ required: true, message: '请输入游戏名称' }]}
          >
            <Input placeholder="例如: ark" />
          </Form.Item>

          <Form.Item
            name="display_name"
            label="显示名称"
            rules={[{ required: true, message: '请输入显示名称' }]}
          >
            <Input placeholder="例如: 方舟：生存进化" />
          </Form.Item>

          <Form.Item
            name="memory_threshold_mb"
            label="内存阈值 (MB)"
            rules={[
              { required: true, message: '请输入内存阈值' },
              { type: 'number', min: 100, message: '内存阈值不能小于100MB' }
            ]}
          >
            <InputNumber
              placeholder="例如: 3800"
              style={{ width: '100%' }}
              min={100}
              max={10000}
            />
          </Form.Item>

          <Form.Item
            name="default_port_range"
            label="默认端口范围"
            rules={[{ pattern: /^\d+-\d+$/, message: '请输入正确的端口范围格式，如: 7777-7877' }]}
          >
            <Input placeholder="例如: 7777-7877" />
          </Form.Item>

          <Form.Item
            name="is_steam_game"
            label="是否为Steam游戏"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="steam_app_id"
            label="Steam App ID"
            rules={[
              { type: 'number', min: 1, message: 'Steam App ID必须大于0' }
            ]}
          >
            <InputNumber
              placeholder="例如: 346110"
              style={{ width: '100%' }}
              min={1}
            />
          </Form.Item>

          <Form.Item
            name="supports_mods"
            label="支持MOD"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="status"
            label="状态"
            rules={[{ required: true, message: '请选择状态' }]}
          >
            <Select placeholder="请选择状态">
              <Select.Option value="active">启用</Select.Option>
              <Select.Option value="inactive">禁用</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="icon"
            label="图标URL"
          >
            <Input placeholder="例如: https://example.com/icon.png" />
          </Form.Item>

          <Form.Item
            name="config_template"
            label="配置模板"
          >
            <Input.TextArea
              placeholder="请输入配置模板内容"
              rows={4}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
          >
            <Input.TextArea
              placeholder="请输入游戏描述"
              rows={3}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default GameManagement;
